'use client'

import { BannerCarousel as BannerCarouselEntity } from '@/entities/banner'
import { cn, formatBreakText } from '@utils'
import { size } from 'lodash-es'
import Image from 'next/image'
import 'swiper/css'
import { Autoplay, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

export default function BannerCarousel({
  className,
  data,
  fullScreen = false,
  children,
  modal = false,
  titleSub,
}: {
  className?: string
  data: BannerCarouselEntity[]
  fullScreen: boolean
  children?: React.ReactNode
  modal?: boolean
  titleSub?: React.ReactNode
}) {
  const onelyOne = size(data) === 1 && !Boolean(children)
  return (
    <section className={cn('h-150 bg-bg-hero relative', className)}>
      {onelyOne ? (
        <Item data={data[0]} fullScreen={fullScreen} />
      ) : (
        <>
          <Swiper
            modules={[Pagination, Autoplay]}
            pagination={{
              enabled: !onelyOne,
              clickable: true,
              el: '#banner-carousel-pagination',
              type: 'bullets',
              bulletClass:
                'w-2 h-2 lg:w-3 lg:h-3 rounded-full cursor-pointer bg-font-primary/30',
              bulletActiveClass: '!bg-white',
            }}
            // 播放持续时间1.5s
            speed={1500}
            autoplay={{ delay: 8000 }}
            loop
            className='w-full h-full'
          >
            {children && (
              <SwiperSlide className='h-full'>{children}</SwiperSlide>
            )}
            {data.map((item, idx) => (
              <SwiperSlide key={idx} className='h-full'>
                <Item
                  data={item}
                  modal={modal}
                  titleSub={titleSub}
                  fullScreen={fullScreen}
                />
              </SwiperSlide>
            ))}
          </Swiper>
          <div
            id='banner-carousel-pagination'
            className='absolute left-1/2 bottom-4 -translate-x-1/2 z-2 flex items-center gap-x-2'
          ></div>
        </>
      )}
    </section>
  )
}

function Item({
  data,
  modal = false,
  titleSub,
  fullScreen = false,
}: {
  data: BannerCarouselEntity
  modal?: boolean
  titleSub?: React.ReactNode
  fullScreen?: boolean
}) {
  return (
    <div className='h-full relative flex flex-col before:h-1/5 md:before:hidden md:justify-center'>
      {(data.video || data.cover) && (
        <>
          {data.video ? (
            <video
              src={data.video}
              className='w-full h-full object-cover absolute inset-0 pointer-events-none'
              autoPlay
              loop
              muted
              poster={data.cover}
            ></video>
          ) : data.cover ? (
            <picture className='w-full h-full absolute inset-0 pointer-events-none'>
              <source
                media='(max-width: 767px)'
                srcSet={data.mobileCover || data.cover}
              />
              <source media='(min-width: 768px)' srcSet={data.cover} />
              <Image
                src={data.cover}
                alt={data.title}
                className='w-full h-full object-cover pointer-events-none'
                unoptimized
                priority
                fill
              />
            </picture>
          ) : null}
          <i className='absolute inset-0 bg-font-primary/60 md:hidden'></i>
        </>
      )}
      <div
        className={cn(
          'relative z-1',
          modal ? 'px-4 lg:px-7.5' : fullScreen ? 'section-banner' : 'section'
        )}
      >
        {data.title && (
          <h1
            className={cn('text-white text-3xl font-bold lg:text-5xl', {
              'lg:max-w-1/2': !modal,
              'text-5xl sm:text-5xl lg:text-6xl xl:text-7xl xl:leading-20':
                fullScreen,
            })}
            style={data.titleColor ? { color: data.titleColor } : {}}
            dangerouslySetInnerHTML={{ __html: formatBreakText(data.title) }}
          ></h1>
        )}
        {data.brief && (
          <h3
            className={cn('text-white text-lg lg:text-xl mt-4 lg:mt-10', {
              'lg:max-w-1/2': !modal,
            })}
            style={data.briefColor ? { color: data.briefColor } : {}}
            dangerouslySetInnerHTML={{ __html: formatBreakText(data.brief) }}
          ></h3>
        )}
        {titleSub}
      </div>
    </div>
  )
}
